import { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Button,
  Text,
  VisuallyHidden,
  Placeholder,
} from "@telegram-apps/telegram-ui";
import { openInvoice } from "@telegram-apps/sdk";
import ApiService from "../../handlers/apiService";
import { PackIdEnum, LookIdEnum } from "../../handlers/types";
import { useI18n } from "../../utils/i18n/I18nProvider";
import "./SpecialDealsModal.css";
import { useNavigate } from "react-router-dom";
import { DialogDescription, DialogTitle } from "@radix-ui/react-dialog";
import StarIcon from "../Shop/StarIcon";

interface SpecialDealsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseSuccess?: () => void;
  hideShopButton?: boolean;
}

interface SaleItem {
  id: PackIdEnum | LookIdEnum;
  type: "pack" | "look";
  nameKey: string;
  price: number;
  discountPrice: number;
  items?: {
    boosts?: {
      heal?: number;
      revive?: number;
      explosion?: number;
      doubling?: number;
      energy_one?: number;
      energy_full?: number;
    };
    looks?: LookIdEnum[];
  };
  characteristicsUpgrades?: Record<string, number>;
  rarity?: string;
  spriteKey?: string;
}

// Pack images mapping
const PACK_IMAGES: Record<PackIdEnum, string> = {
  [PackIdEnum.BASICBOOSTS3]: "/assets/img/ui/bundle.webp",
  [PackIdEnum.BASICBOOSTS6]: "/assets/img/ui/bundle.webp",
  [PackIdEnum.BASICBOOSTS9]: "/assets/img/ui/bundle.webp",
  [PackIdEnum.HEAL10]: "/assets/img/ui/heal.webp",
  [PackIdEnum.REVIVE10]: "/assets/img/ui/revive.webp",
  [PackIdEnum.EXPLOSION10]: "/assets/img/ui/explosion.webp",
};

function useSpecialDeals() {
  const { t } = useI18n();
  const [saleItems, setSaleItems] = useState<SaleItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSaleItems = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await ApiService.getInstance().api.purchasesList();

      // Filter packs that have discount prices
      const salePacks: SaleItem[] = response.packs
        .filter((pack) => pack.discountPrice && pack.discountPrice < pack.price)
        .map((pack) => ({
          id: pack.id,
          type: "pack" as const,
          nameKey: pack.nameKey,
          price: pack.price,
          discountPrice: pack.discountPrice!,
          items: pack.items,
        }));

      // Filter looks that have discount prices and are not owned
      const saleLooks: SaleItem[] = response.looks
        .filter(
          (look) =>
            look.discountPrice &&
            look.discountPrice < look.price &&
            !look.isOwned
        )
        .map((look) => ({
          id: look.id,
          type: "look" as const,
          nameKey: look.nameKey,
          price: look.price,
          discountPrice: look.discountPrice!,
          characteristicsUpgrades: look.characteristicsUpgrades,
          rarity: look.rarity,
          spriteKey: look.spriteKey,
        }));

      setSaleItems([...salePacks, ...saleLooks]);
    } catch (err) {
      setError(t("toolkit.shop.errorLoadingItems"));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSaleItems();
  }, [fetchSaleItems]);

  return {
    saleItems,
    isLoading,
    error,
    refetch: fetchSaleItems,
  };
}

function usePurchase(onSuccess: () => void) {
  const { t } = useI18n();
  const [isPurchasing, setIsPurchasing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const purchaseItem = useCallback(
    async (item: SaleItem) => {
      setIsPurchasing(item.id);
      setError(null);

      try {
        let invoiceUrl: string;

        if (item.type === "pack") {
          const response =
            await ApiService.getInstance().api.purchasesBuyCreate({
              packId: item.id as PackIdEnum,
            });
          invoiceUrl = response.invoiceUrl;
        } else {
          const response = await ApiService.getInstance().api.looksBuyCreate(
            item.id as LookIdEnum
          );
          invoiceUrl = response.invoiceUrl;
        }

        if (invoiceUrl && openInvoice.isAvailable()) {
          const status = await openInvoice(invoiceUrl, "url");
          if (status !== "paid") {
            setError(t("toolkit.shop.errorPayment"));
          } else {
            // Wait for 1 second before updating
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Auto-equip look after successful purchase
            if (item.type === "look" && Game.Core?.selectLook) {
              try {
                await Game.Core.selectLook(item.id as string);
                console.log(`Auto-equipped look: ${item.id}`);
              } catch (equipError) {
                console.error("Failed to auto-equip look:", equipError);
                // Don't show error to user as the purchase was successful
              }
            }

            onSuccess();
          }
        }
      } catch (err) {
        setError(t("toolkit.shop.errorPurchase"));
      } finally {
        setIsPurchasing(null);
      }
    },
    [onSuccess, t]
  );

  return {
    purchaseItem,
    isPurchasing,
    error,
  };
}

function getItemDescription(item: SaleItem, t: any): string {
  if (item.type === "pack") {
    return t(`toolkit.shop.pack.description.${item.id}`, {
      fallback: "",
    });
  } else {
    // For looks, get the primary upgrade description
    const characteristicUpgrades = item.characteristicsUpgrades || {};
    const entries = Object.entries(characteristicUpgrades);

    if (entries.length === 0) return t("looks.noUpgrades");

    // Map of characteristics to their translation keys
    const upgradeTranslationKeys = {
      power: "looks.upgrades.power",
      amount: "looks.upgrades.amount",
      area: "looks.upgrades.area",
      cooldown: "looks.upgrades.cooldown",
      speed: "looks.upgrades.speed",
      duration: "looks.upgrades.duration",
      maxHp: "looks.upgrades.maxHp",
      moveSpeed: "looks.upgrades.moveSpeed",
      luck: "looks.upgrades.luck",
      magnet: "looks.upgrades.magnet",
    };

    // Find the first significant upgrade
    for (const [key, value] of entries) {
      if (value !== undefined && upgradeTranslationKeys[key]) {
        return t(upgradeTranslationKeys[key], {
          defaultValue: key.charAt(0).toUpperCase() + key.slice(1),
        });
      }
    }

    return t("looks.noUpgrades");
  }
}

function getItemImage(item: SaleItem): string {
  if (item.type === "pack") {
    return PACK_IMAGES[item.id as PackIdEnum] || "/assets/img/ui/bundle.webp";
  } else {
    // For looks, we would need to get the character image
    // For now, use a placeholder or default character image
    return "/assets/img/characters/player_idle_01.png";
  }
}

function getItemTitle(item: SaleItem, t: any): string {
  if (item.type === "pack") {
    return t(`toolkit.shop.pack.${item.id}`, {
      fallback: item.nameKey,
    });
  } else {
    return item.nameKey;
  }
}

function getRarityClass(rarity?: string): string {
  if (!rarity) return "";
  return `shop-card--${rarity.toLowerCase()}`;
}

export function SpecialDealsModal({
  isOpen,
  onClose,
  onPurchaseSuccess,
  hideShopButton = false,
}: SpecialDealsModalProps): JSX.Element {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { saleItems, isLoading, error, refetch } = useSpecialDeals();
  const {
    purchaseItem,
    isPurchasing,
    error: purchaseError,
  } = usePurchase(() => {
    onPurchaseSuccess?.();
    refetch();
  });

  const handleOpenShop = () => {
    try {
      // Close the special deals modal first
      onClose();

      navigate("/shop");
    } catch (error) {
      console.error("Failed to navigate to shop:", error);
      // Fallback: just close the modal
      onClose();
    }
  };

  if (!isOpen) return <></>;

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      aria-labelledby="special-deals-title"
    >
      <VisuallyHidden>
        <DialogDescription>
          {t("toolkit.shop.paywall.subtitle")}
        </DialogDescription>
        <DialogTitle>{t("toolkit.shop.paywall.title")}</DialogTitle>
      </VisuallyHidden>

      <div className="special-deals-page">
        {/* Header */}
        <div className="special-deals-header">
          <h1 className="special-deals-title">
            {t("toolkit.shop.paywall.title")}
          </h1>
          <p className="special-deals-subtitle">
            {t("toolkit.shop.paywall.subtitle")}
          </p>
          <button
            className="special-deals-close-button"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            ×
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <Placeholder
            header={t("toolkit.shop.loadingHeader")}
            description={t("toolkit.shop.loadingDescription")}
          />
        ) : error ? (
          <Placeholder
            header={t("common.error")}
            description={error}
            action={<Button onClick={refetch}>{t("common.tryAgain")}</Button>}
          />
        ) : saleItems.length === 0 ? (
          <Placeholder
            header={t("toolkit.shop.paywall.title")}
            description={t("toolkit.shop.paywall.noDealsAvailable")}
          />
        ) : (
          <div className="shop-cards">
            {saleItems.map((item, index) => (
              <div
                key={`${item.type}-${item.id}`}
                className={`shop-card ${getRarityClass(item.rarity)} ${
                  isPurchasing === item.id ? "shop-card--loading" : ""
                }`}
                onClick={() =>
                  isPurchasing === null ? purchaseItem(item) : null
                }
                style={{
                  cursor: isPurchasing === null ? "pointer" : "default",
                }}
              >
                <div className="card-content">
                  {item.type === "look" && item.rarity ? (
                    <div
                      className={`card-rarity card-rarity--${item.rarity.toLowerCase()}`}
                    >
                      {t(`looks.rarity.${item.rarity.toLowerCase()}`, {
                        defaultValue: item.rarity,
                      })}
                    </div>
                  ) : (
                    <div className="card-title">{getItemTitle(item, t)}</div>
                  )}

                  {item.type === "look" && item.spriteKey ? (
                    <div className="card-image card-image--sprite">
                      <div
                        className="card-sprite-frame"
                        style={{
                          background: `url(/assets/img/characters/${item.spriteKey}.webp) -12px -8px`,
                        }}
                        aria-label={`Character: ${item.nameKey}`}
                        role="img"
                      />
                    </div>
                  ) : (
                    <img
                      src={getItemImage(item)}
                      alt={item.nameKey}
                      className="card-image"
                    />
                  )}

                  <p className="card-subtitle">{getItemDescription(item, t)}</p>
                </div>

                <div className="card-price-button">
                  <div className="card-price">
                    {isPurchasing === item.id ? (
                      <span className="card-loader"></span>
                    ) : (
                      <>
                        <span className="card-price--original">
                          {item.price}
                        </span>
                        &nbsp;
                        <span>{item.discountPrice}</span>
                      </>
                    )}
                    <StarIcon />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Open shop button */}
        {!hideShopButton && (
          <div className="special-deals-footer">
            <Button
              size="l"
              stretched
              className="special-deals-see-all-button"
              onClick={handleOpenShop}
            >
              {t("common.shop")}
            </Button>
          </div>
        )}

        {/* Error display */}
        {purchaseError && (
          <Text className="special-deals-error" color="danger">
            {purchaseError}
          </Text>
        )}
      </div>
    </Modal>
  );
}
